import { useZero } from "@rocicorp/zero/react";
import { useMemo } from "react";
import { sharedMutators } from "../../../shared/mutators";
import { queries } from "../../../shared/queries";
import { useCurrentUserGrants } from "../../hooks/useCurrentUserGrants";
import { useCurrentUserGroups } from "../../hooks/useCurrentUserGroups";
import { useDefaultConnectorGrants } from "../../hooks/useDefaultConnectorGrants";
import { useHasAccess } from "../../hooks/useHasAccess";
import { useQuery } from "../../hooks/useQuery";
import { getWritableScopeIds } from "../../lib/getWritableScopeIds";
import { ConnectorsBar } from "../ConnectorsBar";

type Props = {
  personalChatId: string;
};

export function PersonalChatConnectorsBar({ personalChatId }: Props) {
  const z = useZero();
  const canCreateApp = useHasAccess("tenant:connected_app_create");
  const defaultConnectorGrants = useDefaultConnectorGrants(undefined);
  const { currentUser } = useCurrentUserGroups();

  // A chat can only use connectors the current user can use, so the picker
  // offers just those under an "Available to you" heading.
  const [allApps] = useQuery(queries.connectorsIndex());
  const myGrants = useCurrentUserGrants();
  const usableConnectorIds = useMemo(
    () =>
      getWritableScopeIds(
        myGrants,
        allApps.map((app) => app.id),
        "connected_app:use"
      ),
    [myGrants, allApps]
  );

  const [personalChat] = useQuery(queries.loadPersonalChat({ personalChatId }));

  const connectorRows = useMemo(
    () => personalChat?.appUsages ?? [],
    [personalChat?.appUsages]
  );
  const connectedAppIds = useMemo(
    () => new Set(connectorRows.map((r) => r.connectedAppId)),
    [connectorRows]
  );

  const connectors = useMemo(
    () =>
      connectorRows.flatMap((row) =>
        row.connectedApp ? [row.connectedApp] : []
      ),
    [connectorRows]
  );

  const associate = (connectedAppId: string) =>
    z.mutate(
      sharedMutators.associatePersonalChatWithConnectedApp({
        personalChatId,
        connectedAppId,
      })
    );

  const dissociate = (connectedAppId: string) =>
    z.mutate(
      sharedMutators.dissociatePersonalChatFromConnectedApp({
        personalChatId,
        connectedAppId,
      })
    );

  return (
    <ConnectorsBar
      parentNoun="chat"
      canWrite={!!currentUser}
      canCreateApp={canCreateApp}
      defaultConnectorGrants={defaultConnectorGrants}
      connected={connectors}
      excludeIds={connectedAppIds}
      availableIds={usableConnectorIds}
      availableLabel="Available to you"
      associate={associate}
      remove={dissociate}
      menuMargin={6}
      subtleAddButton
    />
  );
}
